import React, { useState } from 'react';
import { testEmailSend } from '../../../services/emailService';

const GmailSMTPTestPanel = ({ isOpen, onClose }) => {
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, testing, success, error
  const [selectedTemplate, setSelectedTemplate] = useState('default'); // default, template1, template2, ...

  const handleTestConnection = async () => {
    if (!testEmail || !testEmail?.includes('@')) {
      setTestResult({
        success: false,
        message: '❌ Bitte geben Sie eine gültige E-Mail-Adresse ein'
      });
      return;
    }

    setTesting(true);
    setConnectionStatus('testing');
    setTestResult(null);

    try {
      console.log('🧪 Starting Google Workspace SMTP test to:', testEmail);
      console.log('📧 Using template:', selectedTemplate);
      const result = await testEmailSend(testEmail, selectedTemplate);
      
      setTestResult(result);
      setConnectionStatus(result?.success ? 'success' : 'error');
      
      console.log('📊 Test result:', result);
    } catch (error) {
      console.error('❌ Test failed:', error);
      setTestResult({
        success: false,
        message: `❌ Fehler: ${error?.message || 'Unbekannter Fehler'}`
      });
      setConnectionStatus('error');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = () => {
    setTestEmail('');
    setTestResult(null);
    setConnectionStatus('idle');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Google Workspace SMTP Test</h2>
                <p className="text-blue-100 text-sm mt-1">Verbindung testen & E-Mail-Zustellung prüfen (thg@gridbalance.de)</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Connection Status Indicator */}
          <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${
                  connectionStatus === 'idle' ? 'bg-gray-400' :
                  connectionStatus === 'testing' ? 'bg-yellow-400 animate-pulse' :
                  connectionStatus === 'success'? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="font-semibold text-gray-700">
                  {connectionStatus === 'idle' && 'Bereit zum Testen'}
                  {connectionStatus === 'testing' && 'Teste Verbindung...'}
                  {connectionStatus === 'success' && 'Verbindung erfolgreich'}
                  {connectionStatus === 'error' && 'Verbindung fehlgeschlagen'}
                </span>
              </div>
              {connectionStatus !== 'idle' && (
                <button
                  onClick={handleReset}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          </div>

          {/* Test Email Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Test-E-Mail-Adresse
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e?.target?.value)}
              placeholder="ihre-email@beispiel.de"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              disabled={testing}
            />
            <p className="text-sm text-gray-500 mt-2">
              Geben Sie eine E-Mail-Adresse ein, an die die Test-E-Mail gesendet werden soll.
            </p>
          </div>

          {/* Test Button */}
          <button
            onClick={handleTestConnection}
            disabled={testing || !testEmail}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            {testing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                <span>Teste Verbindung...</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>SMTP-Verbindung testen</span>
              </>
            )}
          </button>

          {/* Test Result */}
          {testResult && (
            <div className={`mt-6 p-4 rounded-lg ${
              testResult?.success 
                ? 'bg-green-50 border-2 border-green-200' :'bg-red-50 border-2 border-red-200'
            }`}>
              <div className="flex items-start gap-3">
                {testResult?.success ? (
                  <svg className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <div className="flex-1">
                  <p className={`font-semibold ${
                    testResult?.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {testResult?.success ? '✅ Test erfolgreich!' : '❌ Test fehlgeschlagen'}
                  </p>
                  <p className={`mt-2 text-sm whitespace-pre-line ${
                    testResult?.success ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {testResult?.message || testResult?.error}
                  </p>
                  {!testResult?.success && testResult?.error && (
                    <div className="mt-4 p-3 bg-white rounded border border-red-200">
                      <p className="text-xs font-semibold text-red-900 mb-2">🔧 Lösungsschritte:</p>
                      <ol className="text-xs text-red-800 space-y-1 list-decimal list-inside">
                        <li>Öffnen Sie das Terminal im Projektverzeichnis</li>
                        <li>Führen Sie aus: <code className="bg-red-100 px-1 rounded font-mono">supabase functions deploy send-email --no-verify-jwt</code></li>
                        <li>Setzen Sie SMTP Secrets in Supabase Dashboard → Project Settings → Edge Functions → Secrets</li>
                        <li>Erforderliche Secrets: SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD, SMTP_FROM_EMAIL, SMTP_FROM_NAME</li>
                        <li>Details siehe: <code className="bg-red-100 px-1 rounded font-mono">GMAIL_SMTP_SETUP.md</code></li>
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Was wird getestet?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Gmail SMTP-Verbindung (Port 587/465)</li>
                  <li>Authentifizierung mit App-Passwort</li>
                  <li>E-Mail-Zustellung an Zieladresse</li>
                  <li>Edge Function Deployment-Status</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Configuration Hints */}
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-start gap-3">
              <svg className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">Voraussetzungen:</p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>Edge Function "send-email" muss deployed sein</li>
                  <li>Gmail App-Passwort muss konfiguriert sein</li>
                  <li>SMTP-Secrets müssen in Supabase gesetzt sein</li>
                </ul>
                <p className="mt-2 text-xs">
                  Siehe <span className="font-mono bg-yellow-100 px-1 rounded">GMAIL_SMTP_SETUP.md</span> für Details
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-4 rounded-b-xl border-t">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Letzte Aktualisierung: {new Date()?.toLocaleString('de-DE')}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GmailSMTPTestPanel;